
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { App, UserApp } from '@/types/app';

export const useApps = () => {
  const { data: apps, isLoading, error } = useQuery({
    queryKey: ['apps'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('apps')
        .select('*')
        .eq('is_active', true)
        .order('display_name');
      
      if (error) throw error;
      return data as App[];
    },
  });

  return { apps: apps || [], isLoading, error };
};

export const useUserApps = () => {
  const queryClient = useQueryClient();

  const { data: userApps, isLoading, error } = useQuery({
    queryKey: ['user-apps'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_apps')
        .select(`
          *,
          app:apps(*)
        `)
        .eq('is_enabled', true);
      
      if (error) throw error;
      return data as (UserApp & { app: App })[];
    },
  });

  const installApp = useMutation({
    mutationFn: async (appId: string) => {
      const { data, error } = await supabase
        .from('user_apps')
        .insert({
          app_id: appId,
          user_id: (await supabase.auth.getUser()).data.user?.id!,
          is_enabled: true
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-apps'] });
      toast.success('App installed successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to install app: ' + error.message);
    }
  });

  const uninstallApp = useMutation({
    mutationFn: async (appId: string) => {
      const { error } = await supabase
        .from('user_apps')
        .delete()
        .eq('app_id', appId)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id!);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-apps'] });
      toast.success('App uninstalled successfully');
    },
    onError: (error: any) => {
      toast.error('Failed to uninstall app: ' + error.message);
    }
  });

  const toggleApp = useMutation({
    mutationFn: async ({ appId, enabled }: { appId: string; enabled: boolean }) => {
      const { error } = await supabase
        .from('user_apps')
        .update({ is_enabled: enabled })
        .eq('app_id', appId)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id!);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-apps'] });
    },
    onError: (error: any) => {
      toast.error('Failed to update app: ' + error.message);
    }
  });

  return {
    userApps: userApps || [],
    isLoading,
    error,
    installApp,
    uninstallApp,
    toggleApp
  };
};
